/**
 * Personnel API Service
 *
 * SWR hooks for fetching healthcare personnel data
 */

import useSWR from 'swr'
import { apiClient } from '../../shared/api/client'
import type { PersonnelOverview, PersonnelTrend } from '../../types/modules'

/**
 * Fetch personnel overview for a given year
 *
 * GET /personnel?year={year}
 */
export function usePersonnelOverview(year: number = 2567) {
  const { data, error, isLoading, mutate } = useSWR<PersonnelOverview>(
    [`/personnel`, { year: year.toString() }],
    apiClient.fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      refreshInterval: 5 * 60 * 1000, // 5 minutes
    }
  )

  return {
    data,
    error,
    isLoading,
    mutate,
  }
}

/**
 * Fetch personnel data for specific district
 *
 * GET /personnel/by-district/{districtId}?year={year}
 */
export function usePersonnelByDistrict(districtId: number, year: number = 2567) {
  const { data, error, isLoading, mutate } = useSWR<PersonnelOverview>(
    districtId ? [`/personnel/by-district/${districtId}`, { year: year.toString() }] : null,
    apiClient.fetcher
  )

  return {
    data,
    error,
    isLoading,
    mutate,
  }
}

/**
 * Fetch personnel distribution for specific category
 *
 * GET /personnel/by-category/{categoryId}?year={year}
 */
export function usePersonnelByCategory(categoryId: number, year: number = 2567) {
  const { data, error, isLoading, mutate } = useSWR(
    categoryId ? [`/personnel/by-category/${categoryId}`, { year: year.toString() }] : null,
    apiClient.fetcher
  )

  return {
    data,
    error,
    isLoading,
    mutate,
  }
}

/**
 * Fetch multi-year personnel trends
 *
 * GET /personnel/trends?district_id={id}&category_id={id}
 */
export function usePersonnelTrends(filters?: {
  districtId?: number
  categoryId?: number
}) {
  const params: Record<string, string> = {}

  if (filters?.districtId) {
    params.district_id = filters.districtId.toString()
  }
  if (filters?.categoryId) {
    params.category_id = filters.categoryId.toString()
  }

  const { data, error, isLoading, mutate } = useSWR<{
    filters: typeof params
    trends: PersonnelTrend[]
  }>(
    [`/personnel/trends`, params],
    apiClient.fetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 60 * 1000, // Cache for 1 minute
    }
  )

  return {
    data,
    error,
    isLoading,
    mutate,
  }
}

/**
 * Fetch personnel trends broken down by category (Top 5 + Others)
 *
 * GET /personnel/trends-by-category?district_id={id}&top_n={n}
 */
export function usePersonnelTrendsByCategory(filters?: {
  districtId?: number
  topN?: number
}) {
  const params: Record<string, string> = {}

  if (filters?.districtId) {
    params.district_id = filters.districtId.toString()
  }
  if (filters?.topN) {
    params.top_n = filters.topN.toString()
  }

  const { data, error, isLoading, mutate } = useSWR<{
    filters: { district_id: number | null; top_n: number }
    top_categories: Array<{ id: number; name_th: string; name_en: string }>
    trends: Array<{
      year: number
      categories: Record<string, number>
      others: number
    }>
  }>(
    [`/personnel/trends-by-category`, params],
    apiClient.fetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 60 * 1000,
    }
  )

  return {
    data,
    error,
    isLoading,
    mutate,
  }
}

/**
 * Fetch personnel data for all districts (for map visualization)
 *
 * GET /personnel/districts?year={year}
 */
export function usePersonnelDistricts(year: number = 2567) {
  const { data, error, isLoading, mutate } = useSWR<{
    year: number
    districts: Array<{
      district_id: number
      name_th: string
      name_en: string
      total_staff: number
      fulltime_count: number
      parttime_count: number
      ratio_per_10k: number
      population: number
      adequacy_level: 'adequate' | 'shortage' | 'critical'
    }>
  }>(
    [`/personnel/districts`, { year: year.toString() }],
    apiClient.fetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 5 * 60 * 1000, // Cache for 5 minutes
    }
  )

  return {
    data,
    error,
    isLoading,
    mutate,
  }
}

/**
 * Fetch personnel categories (reference data)
 *
 * GET /personnel-categories
 */
export function usePersonnelCategories() {
  const { data, error, isLoading } = useSWR(
    '/personnel-categories',
    apiClient.fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshInterval: 60 * 60 * 1000, // Cache for 1 hour
    }
  )

  return {
    data,
    error,
    isLoading,
  }
}
